import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form/form";
import { Modal } from "@/components/base/modal";
import { OrderingList } from "@/components/base/ordering-list";
import { title } from "@/components/base/primitives";
import { useWaitlist } from "@/components/tournaments/context";
import { useTRPC } from "@/integrations/trpc/react";
import { playerName } from "@/utils/profiles";

export function ReorderWaitlist({
  divisionId,
  trigger,
}: {
  divisionId: number;
  trigger: React.ReactNode;
}) {
  const [isOpen, setOpen] = useState(false);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const waitlist = useWaitlist();

  const items =
    waitlist?.map((team) => ({
      value: team.id,
      display: team.team.players
        .map(({ profile }) => playerName(profile))
        .join(" / "),
    })) ?? [];

  const { mutateAsync: reorder } = useMutation({
    ...trpc.tournaments.reorderWaitlist.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.getTeams.pathFilter());
    },
  });

  const form = useAppForm({
    defaultValues: {
      order: items.map(({ value }) => value),
    },
    onSubmit: async ({ value: { order }, formApi }) => {
      await reorder({
        divisionId,
        teams: order.map((id, index) => ({ id, order: index })),
      });
      formApi.reset();
      setOpen(false);
    },
  });

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
      {trigger}

      <Modal size="md" isDismissable isOpen={isOpen} onOpenChange={setOpen}>
        <div className="flex flex-col space-y-4 p-4">
          <Heading className={title({ size: "sm" })} slot="title">
            Reorder Waitlist
          </Heading>

          <form
            className="flex-col space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <form.AppField name="order">
              {(field) => (
                <OrderingList
                  items={items}
                  onChange={(order) => field.handleChange(order)}
                />
              )}
            </form.AppField>
            <form.AppForm>
              <form.Footer>
                <Button onPress={() => setOpen(false)}>Cancel</Button>
                <form.SubmitButton requireChange={false}>
                  Save Order
                </form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </DialogTrigger>
  );
}
